import { useEffect, useMemo, useRef } from 'react';
import { FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import PlaceholderPlantImage from '@/assets/placeholder-plant.jpg';
import { useFormContext } from 'react-hook-form';
import { ImageWithPlaceholder } from '@/features/plants/components/shared/image-with-placeholder.tsx';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';
import { DeviceTreeTable } from '@/features/devices/components/device-tree-table/device-tree-table.tsx';

export const PlantFormStepReviewAndCreatePlant = (_props: { showTitle?: boolean }) => {
  const form = useFormContext<TPlant>();
  const imageValue = form.watch('siteInformation.image');
  const signedImageUrl = form.watch('signedImageUrl');
  const previousBlobUrlRef = useRef<string | null>(null);

  const blobUrl = useMemo(() => {
    if (Array.isArray(imageValue) && imageValue.length > 0) {
      return URL.createObjectURL(imageValue[0]);
    }
    return null;
  }, [imageValue]);

  useEffect(() => {
    if (previousBlobUrlRef.current && previousBlobUrlRef.current !== blobUrl) {
      URL.revokeObjectURL(previousBlobUrlRef.current);
    }
    previousBlobUrlRef.current = blobUrl;

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const imageUrl = useMemo(() => {
    if (signedImageUrl) return signedImageUrl;
    if (blobUrl) return blobUrl;
    return PlaceholderPlantImage;
  }, [signedImageUrl, blobUrl]);

  return (
    <div className="flex flex-col gap-4">
      {/* {Basic Details} */}
      <div className="space-y-6 rounded-lg bg-white p-4 py-4 pb-8">
        <div className="flex flex-col gap-4">
          <h3 className="font-bold">Basic Details</h3>
          <FormField
            control={form.control}
            name="basicDetails.name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Plant name</FormLabel>
                <Input disabled className="bg-white" {...field} />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="basicDetails.type"
            render={() => (
              <FormItem>
                <FormLabel>Plant Type</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('basicDetails.type')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="basicDetails.status"
            render={() => (
              <FormItem>
                <FormLabel>Plant status</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('basicDetails.status')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="basicDetails.shortDescription"
            render={() => (
              <FormItem>
                <FormLabel>Short description</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('basicDetails.shortDescription')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* {Site Information} */}
      <div className="space-y-6 rounded-lg bg-white p-4 py-4 pb-8">
        <div className="flex flex-col gap-4">
          <h3 className="font-bold">Site Information</h3>
          <FormField
            control={form.control}
            name="siteInformation.image"
            render={() => (
              <>
                <FormItem>
                  <FormLabel>Plant image</FormLabel>
                  <ImageWithPlaceholder
                    className="h-[120px] w-[150px] rounded-lg border"
                    src={imageUrl}
                  />
                  <FormMessage />
                </FormItem>
              </>
            )}
          />
          <FormField
            control={form.control}
            name="siteInformation.country"
            render={() => (
              <FormItem>
                <FormLabel>Country</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('siteInformation.country.label')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="siteInformation.state"
            render={() => (
              <FormItem>
                <FormLabel>State</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('siteInformation.state.label')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="siteInformation.city"
            render={() => (
              <FormItem>
                <FormLabel>City</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('siteInformation.city.label')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="siteInformation.coordinates"
            render={() => (
              <FormItem>
                <FormLabel>Longitude and latitude</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={`${form.getValues(
                    `siteInformation.coordinates.longitude`
                  )}, ${form.getValues(`siteInformation.coordinates.latitude`)}`}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="siteInformation.timezone.label"
            render={() => (
              <FormItem>
                <FormLabel>Plant timezone</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('siteInformation.timezone.label')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="siteInformation.siteAddress"
            render={() => (
              <FormItem>
                <FormLabel>Site address</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('siteInformation.siteAddress') ?? undefined}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="siteInformation.siteArea"
            render={() => (
              <FormItem>
                <FormLabel>Site area</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('siteInformation.siteArea') ?? undefined}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="siteInformation.siteAccess"
            render={() => (
              <FormItem>
                <FormLabel>Site Access</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('siteInformation.siteAccess.label')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* {Technical Specifications} */}
      <div className="space-y-6 rounded-lg bg-white p-4 py-4 pb-8">
        <div className="flex flex-col gap-4">
          <h3 className="font-bold">Technical Specifications</h3>
          <FormField
            control={form.control}
            name="technicalSpecifications.capacity"
            render={() => (
              <>
                <FormItem>
                  <FormLabel>Capacity (KW)</FormLabel>
                  <Input
                    disabled
                    className="bg-white"
                    value={form.getValues('technicalSpecifications.capacity')}
                  />
                  <FormMessage />
                </FormItem>
              </>
            )}
          />
          <FormField
            control={form.control}
            name="technicalSpecifications.storageCapacity"
            render={() => (
              <FormItem>
                <FormLabel>Storage capacity</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={
                    form.getValues('technicalSpecifications.storageCapacity') ?? undefined
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Equipment and suppliers */}
      <div className="space-y-6 rounded-lg bg-white p-4 py-4 pb-8">
        <div className="flex flex-col gap-4">
          <h3 className="font-bold">Equipment and Suppliers</h3>
          <FormField
            control={form.control}
            name={`equipmentAndConnectionDetails.devices`}
            render={({ field }) => (
              <FormItem>
                <FormLabel> Device type</FormLabel>
                <DeviceTreeTable data={field.value} />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Financial Information */}
      <div className="space-y-6 rounded-lg bg-white p-4 py-4 pb-8">
        <div className="flex flex-col gap-4">
          <h3 className="font-bold">Financial Information</h3>
          <FormField
            control={form.control}
            name="financialInformation.tariffRate"
            render={() => (
              <FormItem>
                <FormLabel className="required">
                  Tariff rate <span className="font-normal">(€ per kWh)</span>
                </FormLabel>
                <Input
                  disabled
                  value={form.getValues('financialInformation.tariffRate') ?? undefined}
                  className="bg-white"
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="financialInformation.expectedROI"
            render={() => (
              <FormItem>
                <FormLabel>
                  Expected ROI<span className="font-normal"> (%)</span>
                </FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('financialInformation.expectedROI') ?? undefined}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="financialInformation.operationalCosts"
            render={() => (
              <FormItem>
                <FormLabel>
                  Operational costs <span className="font-normal">(€)</span>
                </FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('financialInformation.operationalCosts') ?? undefined}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Contact details */}
      <div className="space-y-6 rounded-lg bg-white p-4 py-4 pb-8">
        <div className="flex flex-col gap-4">
          <h3 className="font-bold">Contact details</h3>
          <div className="flex w-full flex-wrap gap-4">
            <FormField
              control={form.control}
              name="contactDetails.owner.firstName"
              render={() => (
                <FormItem className="grow">
                  <FormLabel className="required">Owner first name</FormLabel>
                  <Input
                    disabled
                    className="bg-white"
                    placeholder="Enter the owner name"
                    value={form.getValues('contactDetails.owner.firstName')}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contactDetails.owner.lastName"
              render={() => (
                <FormItem className="grow">
                  <FormLabel className="required">Owner last name</FormLabel>
                  <Input
                    disabled
                    className="bg-white"
                    placeholder="Enter the owner name"
                    value={form.getValues('contactDetails.owner.lastName')}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="contactDetails.ownerEmail"
            render={() => (
              <FormItem>
                <FormLabel>Owner email</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('contactDetails.ownerEmail')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="contactDetails.ownerPhoneNumber"
            render={() => (
              <FormItem>
                <FormLabel>Owner phone number</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('contactDetails.ownerPhoneNumber')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="contactDetails.installer"
            render={() => (
              <FormItem>
                <FormLabel>Installer name</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('contactDetails.installer')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="contactDetails.installerEmail"
            render={() => (
              <FormItem>
                <FormLabel>Installer email</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('contactDetails.installerEmail')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="contactDetails.installerPhoneNumber"
            render={() => (
              <FormItem>
                <FormLabel>Installer phone number</FormLabel>
                <Input
                  disabled
                  className="bg-white"
                  value={form.getValues('contactDetails.installerPhoneNumber')}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};
